import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import MediaGrid from '@/components/base/MediaGrid';
import EmptyState from '@/components/base/EmptyState';
import { getMediaById, getRelated } from '@/lib/media';
import { useFavorites } from '@/hooks/useFavorites';
import { TYPE_LABELS } from '@/types/media';

export default function Detail() {
  const { id = '' } = useParams();
  const media = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (media) {
      document.title = `${media.title} - 详情 - 神马视频`;
    }
  }, [media]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!media) {
    return (
      <div className="min-h-screen flex flex-col bg-background-100">
        <Header />
        <main className="flex-1 w-full px-4 md:px-8 py-16">
          <EmptyState
            icon="ri-film-line"
            title="内容不存在"
            description="该影视内容可能已被移除，去首页看看其他精彩内容吧。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            }
          />
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelated(media, 4);
  const active = isFavorite(media.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('详情页链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  const handleFavorite = () => {
    toggleFavorite(media.id);
    setToast(active ? '已取消收藏' : '已加入收藏');
  };

  const metaItems = [
    { label: '导演', value: media.director },
    { label: '主演', value: media.cast.join(' / ') },
    { label: '类型', value: media.genres.join(' / ') },
    { label: '地区', value: media.region },
    { label: '年份', value: String(media.year) },
    { label: '状态', value: media.episodes },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-100">
      <Header />

      <div className="w-full px-4 md:px-8 py-3 border-b border-background-200 bg-background-50">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <Link to={`/category/${media.type}`} className="hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
            {TYPE_LABELS[media.type]}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-900 font-medium whitespace-nowrap">{media.title}</span>
        </nav>
      </div>

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <div className="rounded-lg bg-background-50 border border-background-200/80 p-4 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-10">
            <div className="mx-auto lg:mx-0 w-[220px] sm:w-[260px] lg:w-[300px] aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={media.cover}
                alt={`${media.title} 海报`}
                title={`${media.title} - 神马视频影视资源`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">{media.title}</h1>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs md:text-sm font-medium">
                  {TYPE_LABELS[media.type]}
                </span>
                {media.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs md:text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm md:text-base">
                <span className="inline-flex items-center gap-1.5 text-accent-600 font-bold text-lg md:text-xl">
                  <i className="ri-star-fill"></i>
                  {media.rating}
                  <span className="text-xs md:text-sm font-normal text-foreground-500">分</span>
                </span>
                <span className="text-foreground-500">
                  <i className="ri-calendar-line mr-1"></i>
                  {media.updateDate} 更新
                </span>
              </div>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex gap-2 text-sm">
                    <dt className="shrink-0 text-foreground-500 w-12">{item.label}</dt>
                    <dd className="text-foreground-900 min-w-0 break-words">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                      : 'bg-background-100 border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600'
                  }`}
                >
                  <i className={active ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                  {active ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i>
                  分享链接
                </button>
                <Link
                  to={`/category/${media.type}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-list-check-2"></i>
                  更多{TYPE_LABELS[media.type]}
                </Link>
              </div>

              <p className="mt-4 text-xs text-foreground-400">
                提示：本站仅提供影视内容信息展示与导航，不提供视频播放及下载服务。
              </p>
            </div>
          </div>
        </div>

        <section className="mt-8">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
            剧情简介
          </h2>
          <div className="rounded-lg bg-background-50 border border-background-200/80 p-4 md:p-6">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{media.description}</p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              本片汇集了精良的制作团队与实力主创阵容，在题材与叙事上做出了新的探索。无论是画面质感还是情感表达，都值得影视爱好者了解与关注。更多相关资讯与同类推荐，可在下方内容中继续浏览。
            </p>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
            相关推荐
          </h2>
          <MediaGrid items={related} columns={4} />
        </section>
      </main>

      <Footer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in">
          <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-foreground-950 text-background-50 text-sm">
            <i className="ri-checkbox-circle-fill text-accent-400"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}